import React from 'react';
import { motion } from 'framer-motion';
import { 
  Tv, 
  WashingMachine, 
  Music, 
  Laptop, 
  Monitor, 
  Wifi, 
  CheckCircle2, 
  Zap, 
  ShieldCheck, 
  Clock, 
  PhoneCall,
  MapPin
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { electronicsServices, itServices } from '@/data/index';
import { ServiceCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const springPresets = {
  gentle: { stiffness: 300, damping: 35 },
  stagger: {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};

export default function Services() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.APPLIANCE_SERVICE_1} 
            alt="Service Center" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Our Professional Services
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            Expert repair and support for all your home electronics and IT infrastructure.
            On-site visits and 24/7 on-call assistance.
          </motion.p>
        </div>
      </section>

      {/* Quick Stats/Features */}
      <section className="py-12 border-y border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[ 
              { icon: <Zap className="w-5 h-5 text-primary" />, label: "Same Day Repair" },
              { icon: <ShieldCheck className="w-5 h-5 text-primary" />, label: "90-Day Warranty" },
              { icon: <Clock className="w-5 h-5 text-primary" />, label: "24/7 Support" },
              { icon: <MapPin className="w-5 h-5 text-primary" />, label: "Local On-Site" }
            ].map((stat, i) => (
              <div key={i} className="flex items-center justify-center gap-3">
                {stat.icon}
                <span className="font-medium">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Electronics Repair Section */}
      <section id="electronics" className="py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
                <Tv className="w-4 h-4" />
                Home Electronics
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Expert Electronics Repair</h2>
              <p className="text-muted-foreground text-lg">
                From high-end entertainment systems to essential kitchen appliances, 
                our technicians restore your electronics to peak performance using genuine parts.
              </p>
            </div>
            <Link to={ROUTE_PATHS.CONTACT}>
              <Button variant="outline" className="group">
                Request On-Site Service
                <Zap className="ml-2 w-4 h-4 group-hover:text-yellow-500 transition-colors" />
              </Button>
            </Link>
          </div>

          <motion.div 
            variants={springPresets.stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {electronicsServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* IT Support Section */}
      <section id="it-support" className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row-reverse justify-between items-end mb-12 gap-6">
            <div className="max-w-2xl text-right md:text-right">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4 ml-auto">
                <Monitor className="w-4 h-4" />
                IT Support & Solutions
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Comprehensive IT Support</h2>
              <p className="text-muted-foreground text-lg">
                Hardware upgrades, software troubleshooting, and network optimization. 
                We provide both remote call support and professional on-site IT interventions.
              </p>
            </div>
            <Link to={ROUTE_PATHS.CONTACT}>
              <Button variant="outline" className="group">
                Get Technical Support
                <PhoneCall className="ml-2 w-4 h-4 group-hover:text-primary transition-colors" />
              </Button>
            </Link>
          </div>

          <div className="relative mb-16 rounded-3xl overflow-hidden h-[300px]">
            <img 
              src={IMAGES.IT_SUPPORT_1} 
              alt="IT Support Specialist" 
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background/50 flex items-center px-12">
              <div className="max-w-md">
                <h3 className="text-2xl font-bold mb-4">Remote Diagnostic Available</h3>
                <p className="text-muted-foreground">
                  Need immediate help? Our experts can often solve software and network issues remotely via encrypted on-call support.
                </p>
              </div>
            </div>
          </div>

          <motion.div 
            variants={springPresets.stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {itServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-muted-foreground">A simple 4-step process to get your devices back in action.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {[
              { step: "01", title: "Book a Call", desc: "Contact us via phone or our online form to describe the issue." },
              { step: "02", title: "Expert Diagnostic", desc: "A technician evaluates the problem on-site or via remote access." },
              { step: "03", title: "Fast Repair", desc: "We fix the issue using professional tools and high-quality parts." },
              { step: "04", title: "Quality Check", desc: "Thorough testing ensures everything works perfectly before we leave." }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-bold text-primary/5 mb-4 select-none">{item.step}</div>
                <h4 className="text-xl font-bold mb-2">{item.title}</h4>
                <p className="text-muted-foreground">{item.desc}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2">
                    <div className="w-12 h-[2px] bg-border" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to fix your electronics?</h2>
          <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto">
            Join over 10,000 satisfied customers who trust us with their critical home technology and IT systems.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={ROUTE_PATHS.CONTACT}>
              <Button size="lg" variant="secondary" className="text-primary font-bold">
                Schedule a Repair Now
              </Button>
            </Link>
            <a href="tel:1800REPAIR">
              <Button size="lg" variant="outline" className="border-primary-foreground/20 hover:bg-primary-foreground/10 text-primary-foreground">
                Call Support: 1-800-REPAIR
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
